import { ButtonHTMLAttributes, ReactElement } from 'react'
import { DisplayProps, MarginProps, PositionProps } from 'styled-system'

import { TestIdProps, WordBreakProps } from 'designSystem/types/propTypes'

import PDSIcon, {
  IconName,
  IconSize,
} from 'designSystem/components/PDSIcon/PDSIcon'
import PDSText, { TextSize } from 'designSystem/components/PDSText/PDSText'
import INTERNALxButton from 'designSystem/components/internal/elements/INTERNALxButton'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'

type ButtonSize = 'sm' | 'md' | 'lg' | 'sm-f' | 'md-f' | 'lg-f'
type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger'

type BaseProps = ButtonHTMLAttributes<HTMLButtonElement> &
  DisplayProps &
  PositionProps &
  MarginProps &
  TestIdProps &
  WordBreakProps

type Props = {
  iconLeft?: IconName
  iconRight?: IconName
  size?: ButtonSize
  text: string
  variant?: ButtonVariant
} & BaseProps

export default function PDSButton({
  disabled,
  iconLeft,
  iconRight,
  size = 'md',
  testId,
  text,
  variant = 'primary',
  ...props
}: Props): ReactElement {
  const sizeStyles = BUTTON_SIZE[size]
  const variantStyles = BUTTON_VARIANT[variant]

  const iconColor = disabled
    ? variantStyles.text.disabledColor
    : variantStyles.text.color

  return (
    <INTERNALxButton
      alignItems="center"
      border="1px solid"
      borderRadius={SIZE.s4}
      boxShadow="inset 0px -2px 2px -1px rgba(0,0,0,0.1), inset 0px 1px 2px 0px rgba(255,255,255,0.25)"
      className={`button ${variant}`}
      data-testid={testId}
      disabled={disabled}
      display="flex"
      justifyContent="center"
      whiteSpace="nowrap"
      {...sizeStyles}
      {...variantStyles.button}
      {...props}
    >
      {iconLeft && (
        <PDSIcon
          color={iconColor}
          marginRight={SIZE.s4}
          name={iconLeft}
          size={BUTTON_SIZE[size].iconSize}
        />
      )}
      <PDSText
        className="button-text"
        display="block"
        size={BUTTON_SIZE[size].textSize}
        color={variantStyles.text.color}
        weight="semibold"
      >
        {text}
      </PDSText>
      {iconRight && (
        <PDSIcon
          color={iconColor}
          marginLeft={SIZE.s4}
          name={iconRight}
          size={BUTTON_SIZE[size].iconSize}
        />
      )}
    </INTERNALxButton>
  )
}

const BUTTON_VARIANT = {
  primary: {
    button: {
      backgroundColor: COLOR.buttonPrimary,
      borderColor: COLOR.buttonPrimary_border,
    },
    text: {
      disabledColor: COLOR.textButtonPrimary_disabled,
      color: COLOR.textButtonPrimary,
    },
  },
  secondary: {
    button: {
      backgroundColor: COLOR.buttonSecondary,
      borderColor: COLOR.buttonSecondary_border,
    },
    text: {
      disabledColor: COLOR.textButtonSecondary_disabled,
      color: COLOR.textButtonSecondary,
    },
  },
  tertiary: {
    button: {
      backgroundColor: COLOR.buttonTertiary,
      borderColor: COLOR.buttonTertiary_border,
    },
    text: {
      disabledColor: COLOR.textButtonTertiary_disabled,
      color: COLOR.textButtonTertiary,
    },
  },
  danger: {
    button: {
      backgroundColor: COLOR.buttonAlert,
      borderColor: COLOR.buttonAlert_border,
    },
    text: {
      disabledColor: COLOR.textButtonAlert_disabled,
      color: COLOR.white,
    },
  },
}

type ButtonSizeStyles = {
  height: string
  iconSize: IconSize
  padding: string
  textSize: TextSize
  width?: string
}
const BUTTON_SIZE: { [key: string]: ButtonSizeStyles } = {
  sm: {
    height: '1.5rem',
    iconSize: 'sm',
    padding: `${SIZE.s4} ${SIZE.s20}`,
    textSize: 'sm',
  },
  md: {
    height: '2.25rem',
    iconSize: 'md',
    padding: `${SIZE.s8} ${SIZE.s20}`,
    textSize: 'md',
  },
  lg: {
    height: '3rem',
    iconSize: 'md',
    padding: `${SIZE.s12} ${SIZE.s20}`,
    textSize: 'lg',
  },
  'sm-f': {
    height: '1.5rem',
    iconSize: 'sm',
    padding: `${SIZE.s4} 0`,
    textSize: 'sm',
    width: '100%',
  },
  'md-f': {
    height: '2.25rem',
    iconSize: 'md',
    padding: `${SIZE.s8} 0`,
    textSize: 'md',
    width: '100%',
  },
  'lg-f': {
    height: '3rem',
    iconSize: 'md',
    padding: `${SIZE.s12} 0`,
    textSize: 'lg',
    width: '100%',
  },
}
